<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, toRef, watch } from 'vue'

import GoogleLogo from '@/components/GoogleLogo.vue'
import UigIcon from '@/components/UigIcon.vue'
import { useModal } from '@/composables/useModal'
import { legalLinks } from '@/data/legal'
import { isGoogleIdentityConfigured, renderGoogleButton } from '@/services/googleIdentity'
import { onboardingLog } from '@/services/onboardingLog'
import { authProvider, emailSignupEnabled, passwordLoginEnabled } from '@/services/session'
import { useAuthStore, type SignInMode } from '@/stores/auth'

type GoogleButtonState = 'loading' | 'ready' | 'failed'

const auth = useAuthStore()
const open = toRef(auth, 'signInOpen')
const confirmationSent = ref(false)
const googleSlot = ref<HTMLElement | null>(null)
const googleState = ref<GoogleButtonState>('loading')
const emailInput = ref<HTMLInputElement | null>(null)
const passwordInput = ref<HTMLInputElement | null>(null)
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const consent = ref(false)
let revision = 0
let googleRevision = 0
let resizeObserver: ResizeObserver | undefined

const usesGoogleIdentity = authProvider === 'supabase' && isGoogleIdentityConfigured
const googleUnavailable = authProvider === 'supabase' && !isGoogleIdentityConfigured
const emailAuthEnabled = passwordLoginEnabled || emailSignupEnabled
const signingUp = computed(() => emailSignupEnabled && auth.signInMode === 'signup')
const canContinue = computed(() => consent.value && auth.ready && !auth.busy)
const canUseGoogle = computed(() => open.value && !confirmationSent.value && canContinue.value && !googleUnavailable)
const statusMessage = computed(() => {
  if (auth.busy) return signingUp.value ? 'Creating your account…' : 'Signing you in…'
  if (!auth.ready) return 'Restoring your session…'
  if (!confirmationSent.value && usesGoogleIdentity && googleState.value === 'loading') return 'Loading Google sign-in…'
  return ''
})
const errorMessage = computed(() => {
  if (auth.error) return auth.error
  if (!confirmationSent.value && usesGoogleIdentity && googleState.value === 'failed') {
    return 'Google sign-in could not load. Check your connection or disable content blockers, then try again.'
  }
  return ''
})

function close() {
  auth.closeSignIn()
}

useModal(open, close)

function reset() {
  confirmationSent.value = false
  email.value = ''
  password.value = ''
  showPassword.value = false
  consent.value = false
}

async function mountGoogleButton() {
  resizeObserver?.disconnect()
  const attempt = ++googleRevision
  if (!usesGoogleIdentity || !open.value || confirmationSent.value) return
  googleState.value = 'loading'
  await nextTick()
  const slot = googleSlot.value
  if (!slot || attempt !== googleRevision) return
  let width = slot.clientWidth
  try {
    await renderGoogleButton(slot, width, (credential, nonce) => {
      // Результат закрытого окна или прежнего шага не начинает новую попытку входа.
      if (attempt !== googleRevision || !canUseGoogle.value) return
      void auth.signInWithGoogleIdToken(credential, nonce)
    })
    if (attempt !== googleRevision || googleSlot.value !== slot) return
    googleState.value = 'ready'
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (slot.clientWidth === width || auth.busy) return
        width = slot.clientWidth
        void mountGoogleButton()
      })
      resizeObserver.observe(slot)
    }
  } catch {
    if (attempt !== googleRevision || googleSlot.value !== slot) return
    googleState.value = 'failed'
    onboardingLog('warn', 'auth_dialog_google_failed')
  }
}

async function retry() {
  if (auth.busy) return
  auth.error = null
  if (usesGoogleIdentity && googleState.value === 'failed') await mountGoogleButton()
  else passwordInput.value?.focus()
}

function signInWithGoogle() {
  if (canUseGoogle.value) void auth.signIn()
}

async function changeMode(mode: SignInMode) {
  if (auth.busy) return
  auth.signInMode = mode
  confirmationSent.value = false
  password.value = ''
  showPassword.value = false
  auth.error = null
  await nextTick()
  emailInput.value?.focus()
}

async function submitPassword() {
  if (!emailAuthEnabled || !canContinue.value || confirmationSent.value) return
  if (signingUp.value && password.value.length < 8) return
  if (!emailInput.value?.reportValidity() || !passwordInput.value?.reportValidity()) return
  const attempt = revision
  const result = signingUp.value
    ? await auth.signUpWithPassword(email.value.trim(), password.value)
    : await auth.signInWithPassword(email.value.trim(), password.value)
  if (attempt !== revision) return
  password.value = ''
  showPassword.value = false
  if (result === 'confirmation_required') confirmationSent.value = true
}

watch(open, (visible) => {
  revision++
  googleRevision++
  resizeObserver?.disconnect()
  reset()
  onboardingLog('debug', visible ? 'auth_dialog_opened' : 'auth_dialog_closed', { mode: auth.signInMode })
  if (visible) void mountGoogleButton()
}, { flush: 'sync' })

watch(confirmationSent, () => { void mountGoogleButton() }, { flush: 'post' })

watch(() => auth.isAuthenticated, async (signedIn) => {
  if (!signedIn || !open.value) return
  onboardingLog('info', 'auth_dialog_completed', { mode: auth.signInMode })
  close()
  await nextTick()
  const accountLink = [...document.querySelectorAll<HTMLElement>('.account-chip')].find((chip) => chip.offsetParent)
  if (document.activeElement === document.body) accountLink?.focus({ preventScroll: true })
})

onBeforeUnmount(() => {
  googleRevision++
  resizeObserver?.disconnect()
})
</script>

<template>
  <Teleport to="body">
    <Transition name="auth-sheet">
      <div
        v-if="open"
        class="auth-backdrop"
        @click.self="close"
      >
        <section
          class="auth-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="auth-dialog-title"
          aria-describedby="auth-dialog-subtitle"
          :aria-busy="auth.busy || !auth.ready"
        >
          <button
            class="auth-dialog__close"
            type="button"
            aria-label="Close sign-in"
            @click="close"
          >
            <UigIcon
              name="xmark"
              :size="14"
            />
          </button>

          <div class="auth-dialog__content">
            <header class="auth-dialog__header">
              <img
                class="auth-dialog__logo"
                src="/assets/logo/logo.svg"
                alt=""
                width="56"
                height="56"
              >
              <h2 id="auth-dialog-title">
                {{ confirmationSent ? 'Check your email' : 'Welcome to UIG' }}
              </h2>
              <p
                id="auth-dialog-subtitle"
                class="auth-dialog__subtitle"
              >
                {{ confirmationSent ? 'Confirm your email to finish registration' : auth.signInMode === 'signup' ? 'Sign up and generate' : 'Sign in and generate' }}
              </p>
            </header>

            <div
              v-if="confirmationSent"
              class="auth-dialog__confirmation"
              role="status"
            >
              <p>Open the confirmation link in your email. Your account is ready to use after your email is verified.</p>
              <button
                class="auth-dialog__continue"
                type="button"
                @click="changeMode('signin')"
              >
                Back to sign in
              </button>
            </div>
            <template v-else>
              <div
                v-if="usesGoogleIdentity"
                class="auth-dialog__google"
                :class="{ 'auth-dialog__google--busy': !canUseGoogle }"
                :inert="!canUseGoogle"
                :aria-disabled="!canUseGoogle"
                aria-describedby="auth-consent-description"
              >
                <!-- Google рисует кнопку в iframe со своим белым кружком у логотипа и стили не принимает:
                     показываем свою кнопку, а настоящую (невидимую) кладем поверх, чтобы клик шел в GIS. -->
                <span
                  class="google-button auth-dialog__google-visual"
                  aria-hidden="true"
                >
                  <GoogleLogo />
                  Continue with Google
                </span>
                <div
                  ref="googleSlot"
                  class="auth-dialog__google-slot"
                />
                <span
                  v-if="googleState === 'loading'"
                  class="auth-dialog__placeholder"
                  aria-hidden="true"
                />
              </div>
              <button
                v-else
                class="google-button auth-dialog__google-fallback"
                type="button"
                :disabled="!canUseGoogle"
                :aria-describedby="googleUnavailable ? 'auth-google-unavailable' : 'auth-consent-description'"
                @click="signInWithGoogle"
              >
                <GoogleLogo />
                Continue with Google
              </button>
              <p
                v-if="googleUnavailable"
                id="auth-google-unavailable"
                class="auth-dialog__hint"
              >
                Google sign-in is not configured yet.
              </p>

              <template v-if="emailAuthEnabled">
                <p class="auth-dialog__divider">
                  OR
                </p>
                <form
                  class="auth-dialog__email"
                  @submit.prevent="submitPassword"
                >
                  <label class="auth-dialog__field">
                    <span class="auth-dialog__sr-only">Email address</span>
                    <input
                      id="auth-email"
                      ref="emailInput"
                      v-model="email"
                      type="email"
                      autocomplete="username"
                      inputmode="email"
                      autocapitalize="none"
                      spellcheck="false"
                      placeholder="Email address"
                      :disabled="auth.busy || !auth.ready"
                      required
                    >
                  </label>
                  <label class="auth-dialog__field">
                    <span class="auth-dialog__sr-only">Password</span>
                    <input
                      id="auth-password"
                      ref="passwordInput"
                      v-model="password"
                      :type="showPassword ? 'text' : 'password'"
                      :autocomplete="signingUp ? 'new-password' : 'current-password'"
                      :minlength="signingUp ? 8 : undefined"
                      placeholder="Password"
                      :disabled="auth.busy || !auth.ready"
                      :aria-invalid="Boolean(auth.error)"
                      :aria-describedby="auth.error ? 'auth-dialog-error' : signingUp ? 'auth-password-hint' : undefined"
                      required
                    >
                    <button
                      class="auth-dialog__reveal"
                      type="button"
                      :aria-label="showPassword ? 'Hide password' : 'Show password'"
                      :aria-pressed="showPassword"
                      :disabled="auth.busy"
                      @click="showPassword = !showPassword"
                    >
                      <UigIcon
                        :name="showPassword ? 'eye_slash_fill' : 'eye_fill'"
                        :size="18"
                      />
                    </button>
                  </label>
                  <p
                    v-if="signingUp"
                    id="auth-password-hint"
                    class="auth-dialog__hint"
                  >
                    Use at least 8 characters.
                  </p>
                  <button
                    class="auth-dialog__continue"
                    type="submit"
                    :disabled="!canContinue || !email.trim() || !password || (signingUp && password.length < 8)"
                    aria-describedby="auth-consent-description"
                  >
                    {{ auth.busy ? 'Please wait…' : 'Continue' }}
                    <UigIcon
                      name="chevron_right"
                      :size="14"
                    />
                  </button>
                </form>
              </template>

              <div class="auth-dialog__consent">
                <label>
                  <input
                    v-model="consent"
                    type="checkbox"
                    :disabled="auth.busy"
                  >
                  <span id="auth-consent-description">
                    I agree to the
                    <a
                      :href="legalLinks.terms"
                      target="_blank"
                      rel="noopener"
                    >Terms of Use</a>,
                    acknowledge the
                    <a
                      :href="legalLinks.privacy"
                      target="_blank"
                      rel="noopener"
                    >Privacy Policy</a>,
                    and confirm I'm at least 18 years old.
                  </span>
                </label>
              </div>
              <p
                v-if="emailSignupEnabled"
                class="auth-dialog__mode"
              >
                {{ signingUp ? 'Already have an account?' : 'New to UIG?' }}
                <button
                  type="button"
                  :disabled="auth.busy"
                  @click="changeMode(signingUp ? 'signin' : 'signup')"
                >
                  {{ signingUp ? 'Sign in' : 'Sign up' }}
                </button>
              </p>
              <p
                v-else-if="passwordLoginEnabled"
                class="auth-dialog__hint"
              >
                Email sign-in is for existing accounts.
              </p>
              <a
                class="auth-dialog__policy"
                :href="legalLinks.acceptableUse"
                target="_blank"
                rel="noopener"
              >AI Acceptable Use Policy</a>
            </template>

            <p
              v-if="statusMessage"
              class="auth-dialog__status"
              role="status"
            >
              <span
                class="auth-dialog__spinner"
                aria-hidden="true"
              />
              {{ statusMessage }}
            </p>
            <div
              v-if="errorMessage"
              id="auth-dialog-error"
              class="auth-dialog__error"
              role="alert"
            >
              <span>{{ errorMessage }}</span>
              <button
                type="button"
                :disabled="auth.busy"
                @click="retry"
              >
                Try again
              </button>
            </div>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>
